import React, { useCallback, useEffect } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import styled from 'styled-components';
import BeneficiaryMainCard, {
  BeneficiaryMainCardProps,
  getBeneficiaryIcon,
} from './BeneficiaryMainCard';
import {
  AgeTooltip,
  EthnicTooltip,
  GenderTooltip,
  HealthTooltip,
  SocialTooltip,
  WorkTooltip,
} from './Tooltips';
import { Beneficiaries } from './utils/beneficiaryInterfaces';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { GuidanceTextResponse } from '../utils';
import { BENEFICIARY_QUESTION_ID } from '../Narrative/utils';
import ShowGuidanceText from '../Review/ReportCustomQuestionType/ShowGuidanceText';

const StyledDiv = styled.div`
  margin-bottom: 280px;
  .heading-container {
    max-width: 800px;
  }
  .beneficiary-cards {
    display: grid;
    grid-template-columns: 196px 196px 196px;
    grid-gap: 24px;
  }
  .content-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .heading-titles {
    width: 80%;
    max-width: 720px;
    margin: 0 auto ${SPACERS.large};
  }
`;

export interface MainCategoryProps {
  guidanceText: GuidanceTextResponse | undefined;
  isMiniReportBuilder?: boolean;
  beneficiaries: Beneficiaries;
  beneficiary?: string;
  setBeneficiaries: React.Dispatch<React.SetStateAction<Beneficiaries>>;
  goBack: () => unknown;
}

const MainCategory: React.FC<MainCategoryProps> = ({
  isMiniReportBuilder,
  guidanceText,
  beneficiaries,
  setBeneficiaries,
}) => {
  useEffect(() => {
    // scroll to the top
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);
  const handleClick = useCallback(
    (category: BeneficiaryMainCardProps['category']) => {
      setBeneficiaries(s => ({
        ...s,
        [category]: s[category] === undefined ? { categories: [] } : undefined,
      }));
    },
    [setBeneficiaries],
  );
  return (
    <StyledDiv className="beneficiary-main-category">
      <div className="heading-titles">
        <TrueImpactText
          variant="heading-page"
          className={isMiniReportBuilder ? 'question-text' : 'heading-page'}
          align="center"
        >
          To many donors, the people and communities served are as important as
          it's outcomes. Please select the demographic categories relevant to
          your work.
        </TrueImpactText>
        <ShowGuidanceText
          sectionId="beneficiaries"
          questionId={BENEFICIARY_QUESTION_ID}
          guidanceText={guidanceText}
        />
        <TrueImpactText className="body-text" align="center">
          After you select your categories, we’ll ask you to give percentage
          estimates of the groups you serve.
        </TrueImpactText>
      </div>
      <div className="heading-container" />
      <div className="content-wrapper">
        <div className="beneficiary-cards">
          <BeneficiaryMainCard
            category="age"
            text="Age Groups"
            icon={getBeneficiaryIcon('age')}
            selected={beneficiaries.age !== undefined}
            onClick={handleClick}
            tooltipComponent={AgeTooltip}
          />
          <BeneficiaryMainCard
            category="ethnic"
            text="Ethnic &amp; Racial Groups"
            icon={getBeneficiaryIcon('ethnic')}
            selected={beneficiaries.ethnic !== undefined}
            onClick={handleClick}
            tooltipComponent={EthnicTooltip}
          />
          <BeneficiaryMainCard
            category="gender"
            text="Gender &amp; Sexual Identity"
            icon={getBeneficiaryIcon('gender')}
            selected={beneficiaries.gender !== undefined}
            onClick={handleClick}
            tooltipComponent={GenderTooltip}
          />
          <BeneficiaryMainCard
            category="health"
            text="Health"
            icon={getBeneficiaryIcon('health')}
            selected={beneficiaries.health !== undefined}
            onClick={handleClick}
            tooltipComponent={HealthTooltip}
          />
          <BeneficiaryMainCard
            category="social"
            text="Social &amp; Economic Status"
            icon={getBeneficiaryIcon('social')}
            selected={beneficiaries.social !== undefined}
            onClick={handleClick}
            tooltipComponent={SocialTooltip}
          />
          <BeneficiaryMainCard
            category="work"
            text="Work Status &amp; Occupations"
            icon={getBeneficiaryIcon('work')}
            selected={beneficiaries.work !== undefined}
            onClick={handleClick}
            tooltipComponent={WorkTooltip}
          />
        </div>
        {/* This content needs to move into the HubSpot chat widget} */}
        {/* {!isMiniReportBuilder && <BeneficiaryInfoCard />} */}
      </div>
    </StyledDiv>
  );
};

export default MainCategory;
